import { useMemo } from 'react';
import useSearch, { SearchOptions } from 'lib/useSearch';
import {
  countMatches,
  MIN_HIGHLIGHT_QUERY_LENGTH,
} from '../../utils/searchHighlight';
import { SectionSearchDocument } from '../../hooks/useSectionsSearchDocument';

const SEARCH_OPTIONS: SearchOptions<SectionSearchDocument> = {
  keys: ['avatarName', 'sectionName', 'plainText'],
  threshold: 0,
  ignoreLocation: true,
};

export interface SectionSearchResult {
  matchedSectionIds: Set<string>;
  matchCount: number;
}

/**
 * @description searches sections document against `query` via
 * fuse.js and returns the ids of matching sections alongside the total number
 * of individual matches within them, or `null` when `query` is too short to
 * search by.
 */
const useSectionSearch = (
  documents: SectionSearchDocument[],
  query: string
): SectionSearchResult | null => {
  const trimmedQuery = query.trim();
  // a query too short to highlight is also too short to filter by: it would
  // match most of the project and hide the rest with nothing on screen
  // explaining why
  const isSearchable = trimmedQuery.length >= MIN_HIGHLIGHT_QUERY_LENGTH;
  const results = useSearch(
    isSearchable ? documents : [],
    SEARCH_OPTIONS,
    trimmedQuery
  );

  return useMemo(() => {
    if (!isSearchable) return null;

    const matchedSectionIds = new Set(results.map(({ item }) => item.id));

    const matchCount = results.reduce(
      (total, { item }) =>
        total +
        // every field fuse searches is also rendered with highlights, so each
        // one contributes to the total
        countMatches(item.avatarName, trimmedQuery) +
        countMatches(item.sectionName, trimmedQuery) +
        countMatches(item.plainText, trimmedQuery),
      0
    );

    return { matchedSectionIds, matchCount };
  }, [results, trimmedQuery, isSearchable]);
};

export default useSectionSearch;
